import { useCallback, useMemo, useRef } from 'react';
import { useItemPositionInfo } from './use-item-position-info';
import { IListViewContentManagerProps, IItemPositionData, IItemData } from '../types';
import { useForceUpdate } from '../../../../utils';

export function useItemsViewManager(options: IListViewContentManagerProps) {
    const {
        scrollOffset, items, isVirtualized, isHorizontallyAligned, itemHeight,
        itemWidth, containerDimensions, buffer
    } = options;
    const {
        getAvgItemSpace, getItemPositionInfo, onItemRectCapture, getOccupiedSpace,
        normalizeItem, setItemPositionInfo, isContentReadyToView
    } = useItemPositionInfo(options);
    const prevItemsRef = useRef(items);
    const forceUpdate = useForceUpdate();

    const findActualStartIndex = useCallback(() => {
        let acutalStartIndex = 0;

        if (!isVirtualized) {
            return acutalStartIndex;
        }

        items.forEach((item, idx) => {
            const currentItemDimension = getItemPositionInfo(item);
            let startPosition = 0;
            if (currentItemDimension) {
                startPosition = isHorizontallyAligned ? currentItemDimension.position.left : currentItemDimension.position.top;
            }
            if (!startPosition) {
                startPosition = idx * getAvgItemSpace();
            }
            if (startPosition >= scrollOffset) {
                return;
            }
            acutalStartIndex = idx;
        });

        return acutalStartIndex;
    }, [getAvgItemSpace, getItemPositionInfo, isHorizontallyAligned, isVirtualized, items, scrollOffset]);

    const getStartIndex = useCallback((actualStartIndex: number): number => {
        if (!isVirtualized) {
            return 0;
        }
        return Math.max(actualStartIndex - buffer - 1, 0);
    }, [buffer, isVirtualized]);

    const getEndIndex = useCallback((actualStartIndex: number): number => {
        const lastIndex = items.length - 1;
        if (!isVirtualized) {
            return lastIndex;
        }
        const spaceProperty = isHorizontallyAligned ? 'width' : 'height';
        const constantSpace = isHorizontallyAligned ? itemWidth : itemHeight;
        let endIndex = 0;
        const containerSize = containerDimensions[spaceProperty];
        if (constantSpace > 0) {
            endIndex = actualStartIndex + (containerSize / constantSpace);
        } else {
            let occupiedSpace = 0;
            endIndex = actualStartIndex;
            while (endIndex < lastIndex && occupiedSpace < containerSize) {
                const item = items[endIndex];
                const itemInfo = getItemPositionInfo(item);
                occupiedSpace += (itemInfo?.position[spaceProperty] || getAvgItemSpace() || containerSize);
                endIndex++;
            }
        }
        endIndex += buffer;
        return (isNaN(endIndex) || endIndex === Infinity) ? 0 : Math.min(endIndex, lastIndex);
    }, [items, isVirtualized, isHorizontallyAligned, itemWidth, itemHeight, buffer, containerDimensions, getItemPositionInfo, getAvgItemSpace]);

    const getOccupiedSpaceByPositionInfo = useCallback((index: number) => {
        const item = items[index];
        const itemInfo = getItemPositionInfo(item);
        if (!itemInfo) {
            return getOccupiedSpace(0, index);
        }
        const { position: { height, left, top, width } } = itemInfo;
        return isHorizontallyAligned ? left + width : height + top;
    }, [getItemPositionInfo, getOccupiedSpace, isHorizontallyAligned, items]);

    const getPlaceholderSize = useCallback((endIndex: number): IItemPositionData => {
        const renderedItemsSpace = getOccupiedSpaceByPositionInfo(endIndex);
        const totalSpace = getOccupiedSpace(0);
        const placeholderSize = totalSpace - renderedItemsSpace;
        return {
            height: isHorizontallyAligned ? 1 : placeholderSize,
            left: isHorizontallyAligned ? renderedItemsSpace : 0,
            top: isHorizontallyAligned ? 0 : renderedItemsSpace,
            width: isHorizontallyAligned ? placeholderSize : 1
        };
    }, [getOccupiedSpaceByPositionInfo, getOccupiedSpace, isHorizontallyAligned]);

    const getItemsWithinRange = useCallback((start: number, end?: number): IItemData[] => {
        if (items.length === 0) {
            return [];
        }
        end = (end === undefined ? items.length : end);
        const itemsInRange: IItemData[] = [];
        const isItemsChanged = prevItemsRef.current !== items;
        for (let i = start; i <= end; i++) {
            const item = items[i];
            const itemInfo = getItemPositionInfo(item);
            if (!itemInfo || isItemsChanged) {
                const normalizedItem = normalizeItem(item, i, getOccupiedSpace(0, i - 1));
                if (itemInfo) {
                    normalizedItem.position.height = itemInfo.position.height;
                    normalizedItem.position.width = itemInfo.position.width;
                }
                setItemPositionInfo(normalizedItem);
            }
            if (!itemInfo) {
                continue;
            }
            itemsInRange.push(itemInfo);
        }
        if (isItemsChanged) {
            prevItemsRef.current = items;
            forceUpdate();
        }
        return itemsInRange;
    }, [forceUpdate, getItemPositionInfo, getOccupiedSpace, items, normalizeItem, setItemPositionInfo]);

    const { itemsInView, placeholderPosition } = useMemo(() => {
        const actualStartIndex = findActualStartIndex();
        const startIndex = getStartIndex(actualStartIndex);
        const endIndex = getEndIndex(actualStartIndex);
        return ({ itemsInView: getItemsWithinRange(startIndex, endIndex), placeholderPosition: getPlaceholderSize(endIndex) });
    }, [findActualStartIndex, getEndIndex, getItemsWithinRange, getPlaceholderSize, getStartIndex]);

    return { isContentReadyToView, itemsInView, placeholderPosition, onItemRectCapture, getOccupiedSpace };
}
